import { sql } from "drizzle-orm";
import { bigint, index, integer, pgEnum, pgTable, text, timestamp, uniqueIndex, uuid, varchar } from "drizzle-orm/pg-core";
import { buckets } from "./buckets.js";
import { departmentEnum, taskChannelEnum, taskTypeKeyEnum, taskTypes } from "./taskTypes.js";
import { users } from "./users.js";
export const taskStatus = {
    PENDING: "pending",
    IN_PROGRESS: "in_progress",
    SUCCESS: "success",
    FAILED: "failed",
    ALREADY_PROCESSED: "already_processed",
    DELETED: "deleted",
};
/**
 * Task statuses that count as "done" for bucket completion: a bucket may be completed (SUCCESS)
 * only once every task has reached one of these. Intentionally distinct from TasksDb's CAS "active"
 * set [pending, in_progress].
 */
export const terminalTaskStatuses = [taskStatus.SUCCESS, taskStatus.FAILED, taskStatus.ALREADY_PROCESSED, taskStatus.DELETED];
const terminalTaskStatusSet = new Set(terminalTaskStatuses);
/** True when `status` is a terminal (done) task status — see {@link terminalTaskStatuses}. */
export function isTerminalTaskStatus(status) {
    return terminalTaskStatusSet.has(status);
}
function enumToPgEnum(myEnum) {
    return Object.values(myEnum);
}
export const taskStatusEnum = pgEnum("task_status", enumToPgEnum(taskStatus));
/** Length of the `task.title` column; writers that build a title from free text cut it to fit. */
export const TASK_TITLE_MAX_LENGTH = 1020;
export const tasks = pgTable("task", {
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .defaultNow()
        .notNull()
        .$onUpdate(() => new Date()),
    createdBy: uuid("created_by")
        .references(() => users.id)
        .notNull(),
    lastUpdatedBy: uuid("last_updated_by").references(() => users.id),
    type: taskTypeKeyEnum("type")
        .references(() => taskTypes.key)
        .notNull(),
    channel: taskChannelEnum("channel").notNull(),
    department: departmentEnum("department"),
    bucket: bigint("bucket", { mode: "number" }).references(() => buckets.id, { onDelete: "set null" }),
    title: varchar("title", { length: TASK_TITLE_MAX_LENGTH }).notNull(),
    description: varchar("description", { length: 350 }),
    estimatedTime: integer("estimated_time").notNull().default(0),
    skillLevel: varchar("skill_level", { length: 100 }).notNull(),
    priority: integer("priority").notNull(),
    status: taskStatusEnum("status").notNull(),
    assignee: uuid("assignee").references(() => users.id),
    previousAssignees: uuid("previous_assignees")
        .array()
        .notNull()
        .default(sql `ARRAY[]::uuid[]`),
    startTime: timestamp("start_time", { withTimezone: true }),
    endTime: timestamp("end_time", { withTimezone: true }),
    duration: integer("duration").default(0),
    dedupKey: text("dedup_key"),
}, (table) => [
    uniqueIndex("task_automation_dedup_uq")
        .on(table.type, table.dedupKey)
        .where(sql `${table.dedupKey} IS NOT NULL AND ${table.status} NOT IN (${sql.raw(terminalTaskStatuses.map((status) => `'${status}'`).join(", "))})`),
    // Serves the pool query: matches the workflow and returns its tasks in the query's order
    // (priority, then age). Channel and department are checked on the returned rows, so the query never sorts.
    index("task_unassigned_pool_idx")
        .on(table.type, table.priority.desc().nullsFirst(), table.createdAt)
        .where(sql `${table.bucket} IS NULL AND ${table.assignee} IS NULL AND ${table.status} = 'pending'`),
]);
